import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.tsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner.tsx'
import { apiGet, apiPostForm } from '../../lib/api.ts'

interface JobRole {
  id: number
  name: string
}

export default function ProfilePage() {
  const [loading, setLoading] = useState(true)
  const [notice, setNotice] = useState('')
  const [jobRoles, setJobRoles] = useState<JobRole[]>([])
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    city: '',
    gdc_number: '',
    preferred_locations: '',
    preferred_radius_miles: '',
    availability_status: 'available',
    professional_summary: '',
  })

  const load = async () => {
    try {
      const data = await apiGet<{
        locum: {
          first_name: string
          last_name: string
          phone: string
          city: string
          gdc_number: string
          preferred_locations: string
          preferred_radius_miles: number
          availability_status: string
          professional_summary: string
        }
        job_roles: JobRole[]
        locum_role_ids: number[]
      }>('/account/profile.php?format=json')

      setForm({
        first_name: data.locum.first_name || '',
        last_name: data.locum.last_name || '',
        phone: data.locum.phone || '',
        city: data.locum.city || '',
        gdc_number: data.locum.gdc_number || '',
        preferred_locations: data.locum.preferred_locations || '',
        preferred_radius_miles: data.locum.preferred_radius_miles ? String(data.locum.preferred_radius_miles) : '',
        availability_status: data.locum.availability_status || 'available',
        professional_summary: data.locum.professional_summary || '',
      })
      setJobRoles(data.job_roles)
      setSelectedRoleIds(data.locum_role_ids)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((current) =>
      current.includes(roleId) ? current.filter((id) => id !== roleId) : [...current, roleId]
    )
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const payload: Record<string, string> = {
      action: 'update_profile',
      ...form,
    }

    selectedRoleIds.forEach((id, index) => {
      payload[`locum_roles[${index}]`] = String(id)
    })

    try {
      const data = await apiPostForm<{ message: string }>('/account/profile.php?format=json', payload)
      setNotice(data.message)
      await load()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Unable to save profile.')
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-text-900">Profile</h1>
        <p className="mt-1 text-sm text-text-600">Keep your skills, locations, and availability current.</p>
      </div>

      {notice && <div className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{notice}</div>}

      <Card>
        <CardHeader>
          <CardTitle>Profile details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input className="w-full rounded-md border border-surface-600 px-3 py-3" placeholder="First name" value={form.first_name} onChange={(event) => setForm((current) => ({ ...current, first_name: event.target.value }))} required />
              <input className="w-full rounded-md border border-surface-600 px-3 py-3" placeholder="Last name" value={form.last_name} onChange={(event) => setForm((current) => ({ ...current, last_name: event.target.value }))} required />
            </div>
            <input className="w-full rounded-md border border-surface-600 px-3 py-3" placeholder="Phone" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
            <input className="w-full rounded-md border border-surface-600 px-3 py-3" placeholder="City" value={form.city} onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))} />
            <input className="w-full rounded-md border border-surface-600 px-3 py-3" placeholder="GDC number" value={form.gdc_number} onChange={(event) => setForm((current) => ({ ...current, gdc_number: event.target.value }))} />
            <input className="w-full rounded-md border border-surface-600 px-3 py-3" placeholder="Preferred locations" value={form.preferred_locations} onChange={(event) => setForm((current) => ({ ...current, preferred_locations: event.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <input className="w-full rounded-md border border-surface-600 px-3 py-3" type="number" min="0" placeholder="Travel radius" value={form.preferred_radius_miles} onChange={(event) => setForm((current) => ({ ...current, preferred_radius_miles: event.target.value }))} />
              <select className="w-full rounded-md border border-surface-600 px-3 py-3" value={form.availability_status} onChange={(event) => setForm((current) => ({ ...current, availability_status: event.target.value }))}>
                <option value="available">Available</option>
                <option value="limited">Limited</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-text-900">Work skills</p>
              <div className="grid grid-cols-2 gap-2">
                {jobRoles.map((role) => (
                  <label key={role.id} className={`rounded-lg border px-3 py-3 text-sm ${selectedRoleIds.includes(role.id) ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-surface-300'}`}>
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    {role.name}
                  </label>
                ))}
              </div>
            </div>

            <textarea className="w-full rounded-md border border-surface-600 px-3 py-3" rows={4} placeholder="Professional summary" value={form.professional_summary} onChange={(event) => setForm((current) => ({ ...current, professional_summary: event.target.value }))} />

            <Button type="submit" className="w-full bg-brand-600 hover:bg-brand-700">
              Save profile
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
